// Module-level guide store (same rationale as apps/web view-context store:
// one guide can run at a time, imperative senders — the chat rich_content
// sink — must not depend on React render order). Components subscribe via
// `useSyncExternalStore(subscribeGuide, getGuideSnapshot)`.
import { toast } from "sonner";
import i18n from "@/lib/i18n";
import { track } from "@/lib/analytics";
import type { ActiveGuide, GuideEndReason } from "./types";

export interface GuideState {
  guide: ActiveGuide;
  stepIndex: number;
  /**
   * True only on the step the user just landed on via "Previous". An action
   * step (click/appear/navigate) whose condition is ALREADY satisfied would
   * otherwise instantly re-advance — making Previous look like a no-op. While
   * set, that step suppresses its auto-advance and its "Next" button is enabled
   * so the user can move forward manually. Cleared by any forward advance.
   */
  justRetreated: boolean;
}

let state: GuideState | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function subscribeGuide(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getGuideSnapshot(): GuideState | null {
  return state;
}

// localStorage key + CustomEvent that the docked AI chat panel
// (components/ai-management-panel) uses for its open flag. Dispatching the
// event with `detail:false` closes both the card and its launcher; setting
// the key persists it. Kept in sync with that component's PANEL_OPEN_KEY.
const AI_PANEL_OPEN_KEY = "stockline_activity_panel_open";

function closeAiChatPanel(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(AI_PANEL_OPEN_KEY, "false");
  } catch {
    // storage unavailable — the event below still closes it visually
  }
  window.dispatchEvent(
    new CustomEvent("activityPanelToggle", { detail: false }),
  );
}

export function startGuide(guide: ActiveGuide): void {
  if (guide.steps.length === 0) return;
  // A guide already running (replay tap mid-tour, or a second AI-delivered
  // guide) is being displaced — end it first so its dismissed/analytics fire
  // instead of being silently dropped when we overwrite `state`.
  if (state) endGuide("dismissed");
  state = { guide, stepIndex: 0, justRetreated: false };
  markGuideConsumed(guide.guideId);
  // Get the chat out of the way so the guide has the screen.
  closeAiChatPanel();
  // A notice card is not a tutorial run — keep it out of the guide funnel.
  if (!guide.notice) {
    track("guide_started", {
      guide_id: guide.guideId,
      walkthrough_id: guide.walkthroughId ?? null,
      source: guide.source,
      step_count: guide.steps.length,
    });
  }
  emit();
}

export function advanceGuide(): void {
  if (!state) return;
  if (state.stepIndex >= state.guide.steps.length - 1) {
    endGuide("completed");
    return;
  }
  state = { ...state, stepIndex: state.stepIndex + 1, justRetreated: false };
  emit();
}

/** Step back one. No-op on the first step (nothing before it). */
export function retreatGuide(): void {
  if (!state || state.stepIndex === 0) return;
  state = { ...state, stepIndex: state.stepIndex - 1, justRetreated: true };
  emit();
}

export function dismissGuide(): void {
  endGuide("dismissed");
}

function endGuide(reason: GuideEndReason): void {
  if (!state) return;
  const { guide, stepIndex } = state;
  // A notice card is informational — no completion toast (it wasn't a tutorial
  // the user finished) and no funnel analytics.
  if (guide.notice) {
    state = null;
    emit();
    return;
  }
  track(reason === "completed" ? "guide_completed" : "guide_dismissed", {
    guide_id: guide.guideId,
    walkthrough_id: guide.walkthroughId ?? null,
    source: guide.source,
    step_index: stepIndex,
    step_count: guide.steps.length,
  });
  // Reward finishing the whole guide — not dismissals.
  if (reason === "completed") toast.success(i18n.t("guide.completed"));
  state = null;
  emit();
}

// ---------------------------------------------------------------------------
// Auto-start dedupe. The same ui_guide payload is seen by the live execution
// log AND by the persisted assistant message (and again when a session is
// reopened) — a guide must auto-start exactly once per delivery. Persisted in
// sessionStorage so chat remounts within the tab never restart a guide, while
// a fresh tab starts clean.
// ---------------------------------------------------------------------------

const CONSUMED_KEY = "stk-guide-consumed";
const memoryConsumed = new Set<string>();

function readConsumed(): string[] {
  try {
    const raw = sessionStorage.getItem(CONSUMED_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [...memoryConsumed];
  }
}

export function hasConsumedGuide(guideId: string): boolean {
  return memoryConsumed.has(guideId) || readConsumed().includes(guideId);
}

export function markGuideConsumed(guideId: string): void {
  memoryConsumed.add(guideId);
  try {
    const ids = readConsumed();
    if (!ids.includes(guideId)) {
      // Cap the list so a long-lived tab never grows storage unbounded.
      ids.push(guideId);
      sessionStorage.setItem(CONSUMED_KEY, JSON.stringify(ids.slice(-50)));
    }
  } catch {
    // sessionStorage unavailable (private mode quota) — memory set covers it.
  }
}

/** Test-only: reset module state between test cases. */
export function resetGuideStoreForTests(): void {
  state = null;
  memoryConsumed.clear();
  listeners.clear();
}
